import { FC, useState, useCallback, useRef, useMemo } from 'react'
import { Input, Icon, Text, Loader } from '@/client/components'
import { useDebounce, useRouterPush, useOnClickOutside, useGeneralSearchPages } from '@/client/hooks'
import { IHeaderSearch } from '@/client/types'
import { ROUTER_PATH } from '@/client/utils'
import * as S from './styles'

const SearchProduct: FC<{ search: IHeaderSearch }> = ({ search }) => {
  const ref = useRef<HTMLDivElement>(null)
  const { routerPush, defaultLocale } = useRouterPush()
  const [searchValue, setSearchValue] = useState<string>('')
  const debouncedValue = useDebounce(searchValue, 400)
  const { data, isFetching } = useGeneralSearchPages(debouncedValue, 'products')
  const [isClickedOutside, setClickedOutside] = useState(false)
  useOnClickOutside(ref, () => setClickedOutside(true))

  const isOverlayActive = useMemo(() => {
    let active = !!debouncedValue

    if (isClickedOutside === true) {
      active = false
    }

    return active
  }, [debouncedValue, isClickedOutside])

  const handleClick = useCallback(
    (
      subtype:
        | 'page'
        | 'products'
        | 'custom-page'
        | 'success-stories'
        | 'tutorial'
        | 'print-file-error'
        | 'errori-comuni-nei-fi'
        | 'blog',
      url: string
    ) => {
      let path: string = ''
      const pageName = url.split('/')

      if (subtype === 'products') {
        path = ROUTER_PATH.PRODUCTS[defaultLocale] + `/` + pageName[4]
      }

      if (subtype === 'page') {
        path = `/${pageName[3]}`
      }

      if (subtype === 'custom-page') {
        path = ROUTER_PATH.CUSTOM_PAGES[defaultLocale] + `/` + pageName[4]
      }

      if (subtype === 'success-stories') {
        path = ROUTER_PATH.SUCCESS_STORIES[defaultLocale] + `/` + pageName[4]
      }

      if (subtype === 'errori-comuni-nei-fi') {
        path = ROUTER_PATH.PRINT_FILE_ERRORS[defaultLocale] + `/` + pageName[4]
      }

      if (subtype === 'print-file-error') {
        path = ROUTER_PATH.PRINT_FILE_ERRORS[defaultLocale] + `/` + pageName[4]
      }

      if (subtype === 'tutorial') {
        path = ROUTER_PATH.TUTORIAL[defaultLocale] + `/` + pageName[4]
      }

      if (subtype === 'blog') {
        path = ROUTER_PATH.BLOGS[defaultLocale] + `/` + pageName[4]
      }

      routerPush(path)
    },
    [routerPush, defaultLocale]
  )

  // IT Owned It Owned -
  const decodeHtmlEntities = (str) => {
    const txt = document.createElement('textarea')
    txt.innerHTML = str
    return txt.value
  }
  const refineTitle = (titleString) => {
    const refinedTitle = decodeHtmlEntities(titleString)
      .replace(/&#8211;/g, '–') // convert HTML entity to EN DASH
      .replace(/(?:-|–)\s*it\s+owned$/i, '') // remove the suffix
      .trim()
    return refinedTitle
  }

  const normalize = (str?: string) =>
    str
      ?.replace(/[–—]/g, '-') // replace en dash & em dash with hyphen
      .trim()
      .toLowerCase()

  // Set of titles to exclude (normalized)
  const excludedTitles = new Set(
    [
      'Calcolatore Transparency &#8211; IT Owned',
      'Calculadora transparencia &#8211; IT owned',
      'Calculator Transparency – IT Owned'
    ].map((title) => normalize(title))
  )

  return (
    <>
      <S.Overlay $active={isOverlayActive} />
      <div ref={ref}>
        <S.Search role='button' onClick={() => setClickedOutside(false)}>
          <S.SearchInput $active={!!debouncedValue}>
            <Input
              isBlock
              id='input-search'
              name='p'
              type='search'
              placeholder={search.placeholder}
              value={searchValue}
              onChange={(e) => setSearchValue(e.currentTarget.value)}
              autoComplete='off'
              rightElement={
                <S.SearchIcon>{isFetching ? <Loader size='small' /> : <Icon name='search' size='s5' />}</S.SearchIcon>
              }
            />
          </S.SearchInput>

          {data && isClickedOutside === false && (
            <S.SearchResult>
              {data
                ?.filter((item) => {
                  const normalizedTitle = normalize(item.title)
                  const isExcluded = excludedTitles.has(normalizedTitle)
                  return !isExcluded // keep only items NOT in the excluded set
                })
                .map(({ id, title, subtype, url }) => (
                  <S.SearchItem key={id} role='button' onClick={() => handleClick(subtype, url)}>
                    <S.PageName>{refineTitle(title)}</S.PageName>
                  </S.SearchItem>
                ))}

              {data?.length === 0 && (
                <Text align='center' margin='0.5rem 0 0'>
                  {search.no_items_found}
                </Text>
              )}
            </S.SearchResult>
          )}
        </S.Search>
      </div>
    </>
  )
}

export default SearchProduct
